import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle, AlertTriangle, ShieldCheck, Gift, Clock } from "lucide-react";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-US", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function CouponValidate() {
  const { code } = useParams();
  const [coupon, setCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const fetchCoupon = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/coupons/${encodeURIComponent(code)}`);
      setCoupon(data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Cupón no encontrado");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = `Validar ${code} · Dulce Vida`;
    fetchCoupon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const redeem = async () => {
    setRedeeming(true);
    try {
      const { data } = await api.post(`/coupons/${encodeURIComponent(code)}/redeem`);
      setCoupon(data);
      setShowConfirm(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "No se pudo utilizar");
      setError(msg);
      await fetchCoupon();
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-yellow-300">
        <Loader2 className="w-10 h-10 animate-spin" />
      </div>
    );
  }

  // Determine status
  const status = coupon?.status || (error ? "not_found" : "unknown");
  const isActive = status === "active";
  const isRedeemed = status === "redeemed";
  const isExpired = status === "expired";
  const notFound = !coupon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-900 to-black text-white py-6 px-4">
      <div className="max-w-md mx-auto">
        {/* Brand header */}
        <div className="text-center mb-6">
          <div className="inline-block px-4 py-1.5 rounded-full bg-pink-500/15 border border-pink-400/40 text-pink-200 text-[10px] font-bold tracking-[0.3em] uppercase">
            Dulce Vida · Validación
          </div>
          <h1 className="mt-3 text-2xl font-black text-yellow-200">Verificar Cupón</h1>
          <p className="text-white/50 text-xs mt-1 font-mono">{code}</p>
        </div>

        {/* Big status card */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`rounded-3xl p-6 sm:p-8 border-2 shadow-2xl mb-6 ${
            isActive
              ? "border-green-400 bg-gradient-to-br from-green-500/20 via-green-600/10 to-black"
              : isRedeemed
                ? "border-red-500 bg-gradient-to-br from-red-500/20 via-red-700/10 to-black"
                : isExpired
                  ? "border-amber-500 bg-gradient-to-br from-amber-500/15 via-black to-black"
                  : "border-zinc-700 bg-black/60"
          }`}
          data-testid="coupon-status-card"
        >
          {/* Status icon */}
          <div className="flex flex-col items-center text-center">
            {isActive && (
              <>
                <div className="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center shadow-[0_0_50px_-5px_rgba(34,197,94,0.6)] mb-4">
                  <CheckCircle2 className="w-14 h-14 text-white" strokeWidth={2.5} />
                </div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-green-300 font-bold">
                  ✓ DISPONIBLE
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-white mt-2" data-testid="status-available">
                  Cupón VÁLIDO
                </h2>
                <p className="text-green-100/80 text-sm mt-2">
                  Este cupón está activo y listo para usarse.
                </p>
              </>
            )}

            {isRedeemed && (
              <>
                <div className="w-24 h-24 rounded-full bg-red-600 flex items-center justify-center shadow-[0_0_50px_-5px_rgba(239,68,68,0.6)] mb-4">
                  <XCircle className="w-14 h-14 text-white" strokeWidth={2.5} />
                </div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-red-300 font-bold">
                  ✗ YA UTILIZADO
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-white mt-2" data-testid="status-redeemed">
                  Cupón USADO
                </h2>
                <p className="text-red-100/80 text-sm mt-2">
                  Este cupón ya fue redimido previamente.
                </p>
                {coupon?.redeemed_at && (
                  <div className="mt-3 px-3 py-1.5 rounded-lg bg-black/40 text-xs text-white/70 font-mono">
                    {formatDateTime(coupon.redeemed_at)}
                  </div>
                )}
              </>
            )}

            {isExpired && (
              <>
                <div className="w-24 h-24 rounded-full bg-amber-600 flex items-center justify-center mb-4">
                  <Clock className="w-14 h-14 text-white" strokeWidth={2.5} />
                </div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-amber-300 font-bold">
                  EXPIRADO
                </div>
                <h2 className="text-3xl font-black text-white mt-2" data-testid="status-expired">
                  Cupón Vencido
                </h2>
                <p className="text-amber-100/70 text-sm mt-2">
                  Este cupón ya pasó su fecha de vencimiento.
                </p>
              </>
            )}

            {notFound && (
              <>
                <div className="w-24 h-24 rounded-full bg-zinc-700 flex items-center justify-center mb-4">
                  <AlertTriangle className="w-14 h-14 text-zinc-300" strokeWidth={2.5} />
                </div>
                <h2 className="text-2xl font-black text-white" data-testid="status-not-found">
                  Cupón no encontrado
                </h2>
                <p className="text-zinc-400 text-sm mt-2">{error}</p>
              </>
            )}
          </div>
        </motion.div>

        {/* Coupon details */}
        {coupon && (
          <div className="rounded-2xl border border-yellow-700/30 bg-black/60 p-5 mb-6">
            <div className="flex items-center gap-2 text-yellow-300 text-xs font-bold uppercase tracking-wider mb-3">
              <Gift className="w-4 h-4" /> Información del cupón
            </div>
            <dl className="text-sm space-y-2">
              <div className="flex justify-between"><dt className="text-white/50">Valor</dt><dd className="font-black text-yellow-200">${coupon.value}</dd></div>
              <div className="flex justify-between"><dt className="text-white/50">Para</dt><dd className="font-medium text-right">{coupon.full_name}</dd></div>
              {coupon.camper_number_display && (
                <div className="flex justify-between"><dt className="text-white/50">Campista</dt><dd className="font-mono text-yellow-300">{coupon.camper_number_display}</dd></div>
              )}
              <div className="flex justify-between"><dt className="text-white/50">Válido hasta</dt><dd>{formatDateTime(coupon.expires_at)}</dd></div>
              <div className="flex justify-between"><dt className="text-white/50">Premio</dt><dd className="text-pink-300 font-bold">Dulce Vida</dd></div>
            </dl>
          </div>
        )}

        {/* Action: Utilizar */}
        {isActive && !showConfirm && (
          <Button
            onClick={() => setShowConfirm(true)}
            data-testid="redeem-btn"
            className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-black uppercase tracking-wider h-16 text-lg shadow-xl"
          >
            <ShieldCheck className="w-6 h-6 mr-2" /> Utilizar cupón
          </Button>
        )}

        {/* Confirmation modal-like */}
        {isActive && showConfirm && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border-2 border-amber-400 bg-amber-500/10 p-5"
          >
            <div className="flex items-center gap-2 text-amber-200 font-bold mb-2">
              <AlertTriangle className="w-5 h-5" /> ¿Confirmar uso?
            </div>
            <p className="text-amber-100/80 text-sm mb-4">
              Esta acción es <strong>irreversible</strong>. Una vez utilizado, el cupón quedará marcado como usado y no podrá redimirse de nuevo.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={() => setShowConfirm(false)}
                disabled={redeeming}
                data-testid="cancel-redeem-btn"
                className="bg-transparent border-white/30 text-white hover:bg-white/10"
              >
                Cancelar
              </Button>
              <Button
                onClick={redeem}
                disabled={redeeming}
                data-testid="confirm-redeem-btn"
                className="bg-green-500 hover:bg-green-600 text-white font-bold"
              >
                {redeeming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Sí, utilizar
              </Button>
            </div>
          </motion.div>
        )}

        {/* Footer */}
        <p className="mt-8 text-center text-white/30 text-xs">
          Operación Rescate · Iglesia Ven y Ve
        </p>
      </div>
    </div>
  );
}
